import { Injectable, signal } from '@angular/core';
import { EnumObject } from '@card-software/library';
import { forkJoin, Observable } from 'rxjs';
import { EnumsProxyService } from '../proxies/finance.enums.proxy.service';
import { ItemEnumsProxyService } from '../proxies/item-enums.proxy.service';
import { LocaleHelperService } from './locale-helper.service';
import _ from 'lodash';

/** Enum source/domain. Defaults to finance when omitted; 'item' reads Item.API enums (e.g. UnitOfMeasure). */
export type EnumSource = 'finance' | 'item';

@Injectable({
  providedIn: 'root',
})
export class EnumService {
  private readonly financeEnumLocalStorageKey = 'financeEnums';
  private readonly itemEnumLocalStorageKey = 'itemEnums';

  private readonly _enumsReady = signal(false);
  public readonly enumsReady = this._enumsReady.asReadonly();

  constructor(
    private readonly enumsProxyService: EnumsProxyService,
    private readonly itemEnumsProxyService: ItemEnumsProxyService,
    private localeHelperService: LocaleHelperService,
  ) {
    if (localStorage.getItem(this.financeEnumLocalStorageKey) && localStorage.getItem(this.itemEnumLocalStorageKey)) {
      this._enumsReady.set(true);
    }
  }

  public loadEnums(): Observable<boolean> {
    const hasCache = !!localStorage.getItem(this.financeEnumLocalStorageKey) && !!localStorage.getItem(this.itemEnumLocalStorageKey);
    if (!hasCache) {
      this._enumsReady.set(false);
      localStorage.removeItem(this.financeEnumLocalStorageKey);
      localStorage.removeItem(this.itemEnumLocalStorageKey);
    }
    return new Observable<boolean>((observer) => {
      forkJoin([this.enumsProxyService.getAll(), this.itemEnumsProxyService.getAll()]).subscribe({
        next: (response) => {
          localStorage.setItem(this.financeEnumLocalStorageKey, JSON.stringify(response[0]));
          localStorage.setItem(this.itemEnumLocalStorageKey, JSON.stringify(response[1]));
          this._enumsReady.set(true);
          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading enums', error);
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  public getEnum(enumName: string, source: EnumSource = 'finance'): EnumObject {
    const storageKey = source === 'item' ? this.itemEnumLocalStorageKey : this.financeEnumLocalStorageKey;
    const enumObj = JSON.parse(localStorage.getItem(storageKey)!);

    if (!enumObj) {
      this.loadEnums().subscribe({
        next: (response) => {
          if (response) {
            this.getEnum(enumName, source);
          }
        },
        error: (error) => {
          localStorage.removeItem('SierraToken');
          console.error('Error loading enums', error);
        },
      });
      return { name: enumName, values: {} } as EnumObject;
    }
    const obj = enumObj.find((x: { name: string; values: Record<string, string> }) => x.name === enumName);
    if (obj) {
      // Translate enum values
      const translatedValues: Record<string, string> = {};
      for (const key in obj.values) {
        translatedValues[key] = _.startCase(this.localeHelperService.getLocalizedValue(obj.values[key]));
      }
      return { name: obj.name, values: translatedValues };
    } else {
      return { name: enumName, values: {} } as EnumObject;
    }
  }
}
